/**
 * Embue Entity Selection Framework Types
 *
 * This file contains all type definitions and interfaces for the Entity Selection Framework.
 */

import {
  EntityCriteriaCompoundOperatorInput,
  EntityCriteriaSimpleOperatorInput,
  EntitySelectionCriteriaInput,
  ModelExecutionConfigInput,
  RelatedEntityConfigInput,
  TieredEntitySelectionCriterionInput,
  TieredEntitySelectionInput,
} from '../../../types/generated-types';

/**
 * Maps comparison operators to their MongoDB operator equivalents
 */
export const EntityComparator = {
  // Basic comparisons
  EQUAL_TO: 'equal to',
  NOT_EQUAL_TO: 'not equal to',
  GREATER_THAN: 'greater than',
  LESS_THAN: 'less than',
  GREATER_THAN_OR_EQUAL_TO: 'greater than or equal to',
  LESS_THAN_OR_EQUAL_TO: 'less than or equal to',

  // Range operations
  IN: 'in',
  NOT_IN: 'not in',
  IN_RANGE: 'in range',
  NOT_IN_RANGE: 'not in range',
  BETWEEN: 'between',
  NOT_BETWEEN: 'not between',

  // Text operations
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not contains',
  STARTS_WITH: 'starts with',
  ENDS_WITH: 'ends with',
  TEXT_CONTAINS: 'text contains',
  TEXT_STARTS_WITH: 'text starts with',
  TEXT_ENDS_WITH: 'text ends with',
  TEXT_MATCHES: 'text matches',

  // Null checks
  IS_NULL: 'is null',
  IS_NOT_NULL: 'is not null',

  // Array operations
  ARRAY_CONTAINS: 'array contains',
  ARRAY_NOT_CONTAINS: 'array not contains',
  ARRAY_CONTAINS_ALL: 'array contains all',
  ARRAY_SIZE: 'array size',
  ARRAY_SIZE_GREATER_THAN: 'array size greater than',
  ARRAY_SIZE_LESS_THAN: 'array size less than',
  IN_COLLECTION: 'in collection',
  NOT_IN_COLLECTION: 'not in collection',
  CONTAINS_ALL: 'contains all',
  CONTAINS_ANY: 'contains any',

  // Geospatial operations
  NEAR: 'near',
  WITHIN: 'within',
  WITHIN_RADIUS: 'within radius',
  WITHIN_BOUNDS: 'within bounds',

  // New operators
  EXISTS: 'exists',
  NOT_EXISTS: 'does not exist',
  REGEX: 'matches',
  NOT_REGEX: 'does not match',
  ALL: 'all',
  NONE: 'none',
  SOME: 'some',
  NOT_SOME: 'not some',
  EMPTY: 'is empty',
  NOT_EMPTY: 'is not empty',
} as const;

export type EntityComparatorType =
  (typeof EntityComparator)[keyof typeof EntityComparator];

/**
 * Defines the permission levels for editing entity selection criteria
 */
export enum EntityEditMode {
  LOCKED = 0, // No editing allowed
  CRITERIA_ONLY = 1, // Can only edit criteria values, not structure
  UNRESTRICTED = 2, // Full editing capabilities
}

/**
 * Defines the logical operators for combining multiple selection criteria
 */
export enum EntityCriteriaOperator {
  AND = 'AND', // All criteria must match
  OR = 'OR', // Any criteria must match
}

/**
 * Defines the supported value types for comparison operations
 */
export enum EntityValueType {
  NUMBER = 'NUMBER',
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  DATETIME = 'DATETIME',
  STRING = 'STRING',
  PERCENTAGE = 'PERCENTAGE',
  TEMPERATURE = 'TEMPERATURE',
  HUMIDITY = 'HUMIDITY',
  PRESSURE = 'PRESSURE',
  VOLTAGE = 'VOLTAGE',
  CURRENT = 'CURRENT',
  POWER = 'POWER',
  ENERGY = 'ENERGY',
  FLOW_RATE = 'FLOW_RATE',
  CONCENTRATION = 'CONCENTRATION',
  LEVEL = 'LEVEL',
  SPEED = 'SPEED',
  FREQUENCY = 'FREQUENCY',
  AIRFLOW = 'AIRFLOW',
  ENUM = 'ENUM',
  PHYSICAL_ENUM = 'PHYSICAL_ENUM',
  ARRAY = 'ARRAY',
  DURATION = 'DURATION',
  LATITUDE = 'LATITUDE',
  LONGITUDE = 'LONGITUDE',
  COORDINATES = 'COORDINATES',
  STRING_ARRAY = 'STRING_ARRAY',
  NUMBER_ARRAY = 'NUMBER_ARRAY',
  BOOLEAN_ARRAY = 'BOOLEAN_ARRAY',
  DATE_ARRAY = 'DATE_ARRAY',
}

/**
 * Defines valid value ranges for different entity value types
 */
export interface EntityValueTypeRange {
  min: number;
  max: number;
}

export const EntityValueTypeRanges: Record<
  EntityValueType,
  EntityValueTypeRange | null
> = {
  [EntityValueType.NUMBER]: null,
  [EntityValueType.BOOLEAN]: null,
  [EntityValueType.DATE]: null,
  [EntityValueType.DATETIME]: null,
  [EntityValueType.STRING]: null,
  [EntityValueType.PERCENTAGE]: { min: 0, max: 100 },
  [EntityValueType.TEMPERATURE]: { min: -273.15, max: 1000 },
  [EntityValueType.HUMIDITY]: { min: 0, max: 100 },
  [EntityValueType.PRESSURE]: { min: 0, max: 1000000 },
  [EntityValueType.VOLTAGE]: { min: -1000000, max: 1000000 },
  [EntityValueType.CURRENT]: { min: -1000000, max: 1000000 },
  [EntityValueType.POWER]: { min: 0, max: 1000000 },
  [EntityValueType.ENERGY]: { min: 0, max: 1000000 },
  [EntityValueType.FLOW_RATE]: { min: 0, max: 1000000 },
  [EntityValueType.CONCENTRATION]: { min: 0, max: 1000000 },
  [EntityValueType.LEVEL]: { min: 0, max: 100 },
  [EntityValueType.SPEED]: { min: 0, max: 1000000 },
  [EntityValueType.FREQUENCY]: { min: 0, max: 1000000 },
  [EntityValueType.AIRFLOW]: { min: 0, max: 1000000 },
  [EntityValueType.ENUM]: null,
  [EntityValueType.PHYSICAL_ENUM]: null,
  [EntityValueType.ARRAY]: null,
  [EntityValueType.DURATION]: null,
  [EntityValueType.LATITUDE]: { min: -90, max: 90 },
  [EntityValueType.LONGITUDE]: { min: -180, max: 180 },
  [EntityValueType.COORDINATES]: null,
  [EntityValueType.STRING_ARRAY]: null,
  [EntityValueType.NUMBER_ARRAY]: null,
  [EntityValueType.BOOLEAN_ARRAY]: null,
  [EntityValueType.DATE_ARRAY]: null,
};

/**
 * Represents a single selection criterion that can compare a field value against multiple tier values
 */
export type ITieredEntitySelectionCriterion =
  TieredEntitySelectionCriterionInput;

/**
 * Represents a simple combination of criteria using AND/OR/NOR operators
 */
export type IEntityCriteriaSimpleOperator = EntityCriteriaSimpleOperatorInput;

/**
 * Union type representing any valid selection criteria structure
 */
export type IEntitySelectionCriteria = EntitySelectionCriteriaInput;

/**
 * Represents a complex combination of criteria, operators, and nested conditions
 */
export type IEntityCriteriaCompoundOperator =
  EntityCriteriaCompoundOperatorInput;

/**
 * Interface for related entity configuration
 */
export type IRelatedEntityConfig = RelatedEntityConfigInput;

/**
 * Interface for model execution configuration
 */
export type IModelExecutionConfig = ModelExecutionConfigInput;

/**
 * Top-level interface representing a complete tiered entity selection specification
 */
export type ITieredEntitySelection = TieredEntitySelectionInput;

/**
 * MongoDB-specific types for building query operators
 */

export enum EntityOperator {
  AND = 'AND',
  OR = 'OR',
  SIMPLE = 'SIMPLE',
}

export enum RangeOperator {
  IN_RANGE = 'in range',
  NOT_IN_RANGE = 'not in range',
}
